import createModel, { prop } from 'models/lib/create_model';
import createEnum from 'scripts/lib/create_enum';

export const FormSectionTypes = createEnum('text', 'input');
export const FormInputSectionTypes = createEnum('text', 'select', 'checkbox');

// An option with a `dependentAttr` is only available while the input named by
// `dependentAttr` has the value `dependentValue`, or has it among its values
// for a multiple select.
export function getAvailableOptions(options, formData) {
  return (options || []).filter(
    option => !option.dependentAttr || [].concat(formData[option.dependentAttr]).includes(option.dependentValue)
  );
}

export function getInitialFormData(sections) {
  const defaults = (sections || []).reduce((result, section) => {
    if (section.type === FormSectionTypes.input) {
      const defaultValue = section.defaultValue || getEmptyValue(section.input);
      result[section.attr] = section.input.multiple && !Array.isArray(defaultValue) ? [defaultValue] : defaultValue;
    }
    return result;
  }, {});
  return clearUnavailableSelections(sections, defaults);
}

// Only values of options whose dependency is unmet are cleared. Forms without
// dependent options, such as those of lookup adapters, may hold values that
// match no option, and those are still submitted as they are.
//
// Valid forms only depend on preceding selects, so one pass clears every
// unavailable selection. Forms are rendered from app templates at runtime
// without validation though, so passes repeat until one makes no change. Each
// change only removes values, so this always terminates.
export function clearUnavailableSelections(sections, formData) {
  const cleared = (sections || []).reduce((result, section) => {
    const value = result[section.attr];
    if (section.type !== FormSectionTypes.input || section.input.type !== FormInputSectionTypes.select || !value) {
      return result;
    }
    const availableValues = getAvailableOptions(section.input.options, result).map(option => option.value);
    const unavailableValues = (section.input.options || [])
      .map(option => option.value)
      .filter(v => !availableValues.includes(v));
    if (section.input.multiple) {
      const values = [].concat(value);
      const remainingValues = values.filter(v => !unavailableValues.includes(v));
      return remainingValues.length === values.length ? result : { ...result, [section.attr]: remainingValues };
    }
    return unavailableValues.includes(value) ? { ...result, [section.attr]: '' } : result;
  }, formData);
  return cleared === formData ? cleared : clearUnavailableSelections(sections, cleared);
}

export default createModel({
  modelName: 'ExternalActionForm',

  properties: {
    title: prop(String),
    actionUrl: prop(String),
    closeButton: prop(String),
    submitButton: prop(String),
    sections: prop(Object),
  },

  statics: {
    create(attrs) {
      return new this(attrs);
    },
  },
});

function getEmptyValue(input) {
  if (input.type === FormInputSectionTypes.checkbox) {
    return false;
  }
  return input.multiple ? [] : '';
}
